import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, imageUrl, money } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Truck, MapPin, Tag, Check } from "lucide-react";
import { toast } from "sonner";

const FREE_SHIP = 450;
const FLAT_SHIP = 50;

export default function Cart() {
  const { items, remove, setQty, total, clear } = useCart();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [fulfillment, setFulfillment] = useState("ship");
  const [pickupDate, setPickupDate] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);

  const subtotal = total;
  const hasPickupOnly = items.some((i) => i.pickup_only);
  useEffect(() => { if (hasPickupOnly && fulfillment !== "pickup") setFulfillment("pickup"); }, [hasPickupOnly, fulfillment]);
  const discount = promo ? Math.round(subtotal * promo.percent) / 100 : 0;
  const shipping = fulfillment === "pickup" ? 0 : subtotal >= FREE_SHIP ? 0 : FLAT_SHIP;
  const grand = Math.max(0, subtotal - discount + shipping);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setCheckingPromo(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput, subtotal });
      setPromo(data);
      toast.success(`Code ${data.code} applied — ${data.percent}% off!`);
    } catch (e) {
      setPromo(null);
      toast.error(e.response?.data?.detail || "Invalid promo code");
    } finally { setCheckingPromo(false); }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (fulfillment === "pickup" && (!name.trim() || !phone.trim())) {
      toast.error("Please add your name and phone for pickup");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: email,
        customer_name: name,
        customer_phone: phone,
        promo_code: promo ? promo.code : "",
        fulfillment,
        pickup_date: fulfillment === "pickup" ? pickupDate : "",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-5 pt-44 pb-40 min-h-screen text-center">
        <ShoppingBag className="mx-auto text-white/20 mb-6" size={48} />
        <h1 className="font-display text-4xl md:text-5xl mb-4">Your cart is empty</h1>
        <p className="text-white/50 mb-8">Find your next centrepiece in the collection.</p>
        <Link to="/shop" className="btn-pill btn-solid inline-flex items-center gap-2">Browse the shop <ArrowRight size={15} /></Link>
      </div>
    );
  }

  const methodBtn = (key, Icon, label, sub) => {
    const disabled = key === "ship" && hasPickupOnly;
    return (
      <button data-testid={`fulfillment-${key}`} onClick={() => !disabled && setFulfillment(key)} disabled={disabled}
        className={`flex-1 rounded-xl border p-3 text-left transition-all ${disabled ? "opacity-40 cursor-not-allowed border-white/10" : fulfillment === key ? "border-cyan bg-cyan/10" : "border-white/12 hover:border-white/30"}`}>
        <span className="flex items-center gap-2 text-sm"><Icon size={15} className={fulfillment === key ? "text-cyan" : "text-white/50"} /> {label}</span>
        <span className="block text-white/40 text-xs mt-1 font-mono">{sub}</span>
      </button>
    );
  };

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-36 pb-28 min-h-screen">
      <Reveal><h1 className="font-display text-5xl md:text-6xl mb-12">Cart</h1></Reveal>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
              <img src={imageUrl(i.image)} alt={i.name} className="w-24 h-24 rounded-xl object-cover" />
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex justify-between">
                  <div>
                    <span className="mono-label text-white/40">{i.category}</span>
                    <h3 className="font-display text-xl">{i.name}</h3>
                  </div>
                  <button onClick={() => remove(i.product_id)} className="text-white/40 hover:text-reefpink transition-colors" data-testid={`remove-${i.product_id}`}><Trash2 size={18} /></button>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3 border border-white/15 rounded-full px-3 py-1">
                    <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="text-white/60 hover:text-cyan"><Minus size={14} /></button>
                    <span className="font-mono text-sm w-5 text-center">{i.quantity}</span>
                    <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="text-white/60 hover:text-cyan"><Plus size={14} /></button>
                  </div>
                  <span className="font-mono text-cyan">{money(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
          <button onClick={clear} className="mono-label text-white/40 hover:text-reefpink">Clear cart</button>
        </div>

        <div className="glass rounded-2xl p-6 h-fit border border-white/10">
          <h3 className="font-display text-2xl mb-5">Summary</h3>

          {/* Fulfillment method */}
          <p className="mono-label text-white/40 mb-2">Delivery method</p>
          <div className="flex gap-2 mb-4">
            {methodBtn("ship", Truck, "Ship", "$50 · FREE $450+")}
            {methodBtn("pickup", MapPin, "Local Pickup", "FREE · Miami/Hialeah")}
          </div>
          {hasPickupOnly && (
            <p className="text-toxic text-xs font-mono mb-4 flex items-center gap-1.5" data-testid="pickup-only-note">
              <MapPin size={13} /> Your cart has a local-pickup-only item, so shipping is disabled.
            </p>
          )}
          {fulfillment === "pickup" && (
            <div className="space-y-2 mb-4" data-testid="pickup-fields">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="pickup-name" className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm focus:border-cyan outline-none" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" data-testid="pickup-phone" className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm focus:border-cyan outline-none" />
              <label className="mono-label text-white/40 block">Preferred pickup date</label>
              <input value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} type="date" data-testid="pickup-date" className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm focus:border-cyan outline-none [color-scheme:dark]" />
              <p className="text-white/40 text-xs font-mono">We'll confirm your pickup time by email/text.</p>
            </div>
          )}

          {/* Promo code */}
          <p className="mono-label text-white/40 mb-2">Promo code</p>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="e.g. REEF10" data-testid="promo-input"
                className="w-full bg-white/5 border border-white/10 rounded-full pl-8 pr-3 py-2.5 text-sm focus:border-cyan outline-none uppercase" />
            </div>
            <button onClick={applyPromo} disabled={checkingPromo} data-testid="promo-apply" className="btn-pill !py-2.5 whitespace-nowrap disabled:opacity-50">{checkingPromo ? "…" : "Apply"}</button>
          </div>
          {promo && <p className="text-cyan text-xs font-mono mb-4 flex items-center gap-1" data-testid="promo-applied"><Check size={13} /> {promo.code} — {promo.percent}% off applied</p>}

          <div className="border-t border-white/10 pt-4">
            <div className="flex justify-between text-white/60 mb-2"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-cyan mb-2" data-testid="discount-row"><span>Discount ({promo.code})</span><span className="font-mono">−{money(discount)}</span></div>}
            <div className="flex justify-between text-white/60 mb-2">
              <span>{fulfillment === "pickup" ? "Local pickup" : "Shipping (livestock)"}</span>
              <span className="font-mono">{shipping === 0 ? <span className="text-cyan">FREE</span> : money(shipping)}</span>
            </div>
            {fulfillment === "ship" && subtotal < FREE_SHIP && (
              <p className="text-white/40 text-xs mb-2 font-mono">Add {money(FREE_SHIP - subtotal)} more for FREE shipping</p>
            )}
            <div className="flex justify-between text-lg border-t border-white/10 pt-4 mb-6"><span>Total</span><span className="font-mono text-cyan" data-testid="cart-total">{money(grand)}</span></div>
          </div>

          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email for receipt (optional)" data-testid="checkout-email"
            className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm mb-4 focus:border-cyan outline-none" />
          <button onClick={checkout} disabled={busy} data-testid="checkout-btn" className="btn-pill btn-solid w-full flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? "Redirecting…" : <>Pay securely <ArrowRight size={15} /></>}
          </button>
          <p className="text-white/30 text-xs mt-4 text-center font-mono">Secured by Stripe</p>
        </div>
      </div>
    </div>
  );
}
